<template>
  <div
    class="cart-upsell"
    :class="{ 'cart-upsell--qualify': !getHasShortfall }"
  >
    <template v-if="getTargetValueCents > 0">
      <div class="cart-upsell__lead-copy">
        <RichText
          :content="getLeadText"
          :baseClass="'text-commerce-intro'"
          class="text--center text--clr-black"
        />
      </div>
      <div class="cart-upsell__progress-wrapper">
        <CartUpsellProgress
          :max="getTargetValueCents"
          :value="getCartValueCents"
          :iconSrc="getSliderIconSrc"
          :isProduct="!getHasCombiPromo && getHasValuePromo"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { formatPrice } from "../../utils"
import { mapGetters } from "vuex"
import RichText from "@/components/RichText"
import CartUpsellProgress from "./CartUpsellProgress.vue"

import { promoTypes, quantityStatusTypes } from "@/store/constants"

export default {
  props: {},

  components: {
    RichText,
    CartUpsellProgress
  },

  created() {
    this.uiTextContent_manifest = [
      "cart_drawer_upsell_text_lead_shortfall_combi",
      "cart_drawer_upsell_text_lead_shortfall_combi_mystery",
      "cart_drawer_upsell_text_lead_shortfall_gwp",
      "cart_drawer_upsell_text_lead_shortfall_gwp_mystery",
      "cart_drawer_upsell_text_lead_shortfall_shipping",
      "cart_drawer_upsell_text_lead_qualified_combi",
      "cart_drawer_upsell_text_lead_qualified_combi_mystery",
      "cart_drawer_upsell_text_lead_qualified_gwp",
      "cart_drawer_upsell_text_lead_qualified_gwp_mystery",
      "cart_drawer_upsell_text_lead_qualified_shipping",
      "cart_drawer_upsell_text_sub_combi",
      "cart_drawer_upsell_text_sub_gwp",
      "cart_drawer_upsell_text_sub_shipping"
    ]
  },

  computed: {
    ...mapGetters("content", ["GET_PRODUCT_ENTRY", "GET_SITE_CONFIG"]),
    ...mapGetters("cart", [
      "getCartTotalCents",
      "getCartAdjustedTotalCents",
      "getPromoApplicableTotalCents",
      "getGwpPromo",
      "getGwpPromoThresholdCents"
    ]),

    getCartValueCents() {
      // Both GWP and shipping thresholds are exclusive of any gift cards in the cart
      return this.getPromoApplicableTotalCents
    },

    getHasShortfall() {
      return this.getCartValueCents < this.getTargetValueCents
    },

    getHasCombiPromo() {
      // If the GWP threshold is the same as the Free Shipping threshold, FE shows the 'combi' upsell
      return (
        this.getHasValuePromo &&
        this.getGwpPromoThresholdCents === this.getShippingThresholdCents
      )
    },

    getHasValuePromo() {
      // If there is a valid GWP promotion
      return !!(
        this.getGwpPromo &&
        this.getGwpPromo.promotionType == promoTypes.VALUE &&
        this.getGwpPromo.promotionalProductsCollection &&
        this.getGwpPromo.promotionalProductsCollection.items.length > 0 &&
        this.getGwpPromo.promotionalProductsCollection.items[0]
          .quantityStatus !== quantityStatusTypes.OUT_OF_STOCK &&
        this.getGwpPromo.promotionalProductsCollection.items[0].isWaitlisted !==
          true
      )
    },

    getLeadText() {
      if (this.getHasShortfall) {
        const _shortfallCents =
          this.getTargetValueCents - this.getCartValueCents
        return this.UI_TEXT_CONTENT(
          this.getHasCombiPromo
            ? "cart_drawer_upsell_text_lead_shortfall_combi" +
                (this.getGwpPromo.mystery ? "_mystery" : "")
            : this.getHasValuePromo
            ? "cart_drawer_upsell_text_lead_shortfall_gwp" +
              (this.getGwpPromo.mystery ? "_mystery" : "")
            : "cart_drawer_upsell_text_lead_shortfall_shipping",
          [
            {
              tag: "SHORTFALL",
              value: formatPrice(
                { cents: _shortfallCents },
                this.$store.state.locale.localeConfig
              )
            },
            {
              tag: "PRODUCT_TITLE",
              value: this.getPromoProduct && this.getPromoProduct.displayTitle
            }
          ]
        )
      } else {
        return this.UI_TEXT_CONTENT(
          this.getHasCombiPromo
            ? "cart_drawer_upsell_text_lead_qualified_combi" +
                (this.getGwpPromo.mystery ? "_mystery" : "")
            : this.getHasValuePromo
            ? "cart_drawer_upsell_text_lead_qualified_gwp" +
              (this.getGwpPromo.mystery ? "_mystery" : "")
            : "cart_drawer_upsell_text_lead_qualified_shipping",
          [
            {
              tag: "PRODUCT_TITLE",
              value: this.getPromoProduct && this.getPromoProduct.displayTitle
            }
          ]
        )
      }
    },

    getPromoProduct() {
      const promoProduct =
        (this.getGwpPromo &&
          this.getGwpPromo.promotionalProductsCollection &&
          this.getGwpPromo.promotionalProductsCollection.items[0]) ||
        null

      return (
        promoProduct &&
        this.GET_PRODUCT_ENTRY(promoProduct.sys.id, promoProduct.__typename)
      )
    },

    getShippingThresholdCents() {
      return this.GET_SITE_CONFIG.freeShippingThreshold
    },

    getSliderIconSrc() {
      if (this.getHasCombiPromo) {
        return `${process.env.VUE_APP_CLOUDINARY_BASE_PATH}/image/upload/v1646134752/master_platform/icons/shipping.png`
      } else if (this.getHasValuePromo) {
        // return the src of the product image
        return (
          this.getPromoProduct &&
          this.getPromoProduct.images &&
          this.getPromoProduct.images[0].original_secure_url
        )
      } else {
        return `${process.env.VUE_APP_CLOUDINARY_BASE_PATH}/image/upload/v1646134752/master_platform/icons/shipping.png`
      }
    },

    getTargetValueCents() {
      if (this.getHasValuePromo) {
        // If there's a GWP promotion, use the target value (can be used for both GWP and combi promos)
        return this.getGwpPromoThresholdCents
      } else if (this.getShippingThresholdCents) {
        // else use the 'Free Shipping' threshold
        return this.getShippingThresholdCents
      } else {
        // else bleh
        return 0
      }
    }
  }
}
</script>

<style lang="scss">
.cart-upsell {
  // position: sticky;
  // top: 0;
  z-index: 1;
  padding: 8px 16px 16px;
  background-color: getcolour(bleach_white);
  transition: background-color 0.1s linear;
  filter: drop-shadow(0 10px 5px rgba(getcolour(bleach_black), 0.05));
}
</style>
